import { Grid, Skeleton, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { XGrid } from '@AMIEWEB/Common/XGrid/XGrid';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getTimeOffGridColumns } from './TimeOffGridUtils';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useScenarioStatusPending } from '@AMIEWEB/MarginTool/helper';
import React from 'react';
import { GridColumns, GridRowId, GridSortItem } from '@mui/x-data-grid-pro';
import { selectPlacementMarginEditTimeOffs } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.selector';
import { useEditCellRenderStyles } from './EditCellRender/EditCellRenderStyles';
import { isValid } from 'date-fns';
import { placementMarginAssignmentActions } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.redux';
import { checkOverlapForAllRecords, getFormattedTimeOff } from './EditCellRender/helper';
import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import { IPlacementMarginRequestTimeOff } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.model';
import { usePeopleSoftCalculation } from '../../PeopleSoftCalculation/IPeopleSoftCalculationHelper';
import { selectSelectedPlacementMarginScenario } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { useFormContext } from 'react-hook-form';

export const TimeOffGrid = ({ isRefreshInProgress }: { isRefreshInProgress: boolean }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { triggerPeopleSoftCalculation } = usePeopleSoftCalculation();
  const { classes } = useEditCellRenderStyles();
  const isScenarioStatusPending = useScenarioStatusPending();
  const scenario = useSelector(selectSelectedPlacementMarginScenario);
  const editTimeOffs = useSelector(selectPlacementMarginEditTimeOffs);
  const [headerCount, setHeaderCount] = useState<number>(0);
  const [columns, setColumns] = useState<GridColumns>(
    getTimeOffGridColumns(t, isScenarioStatusPending, classes, editTimeOffs),
  );
  const [, setCurrentSortModel] = React.useState<GridSortItem[]>([{ field: 'startDate', sort: 'desc' }]);
  const [selectedIds, setSelectedIds] = useState<GridRowId[]>([]);
  const { setValue, register } = useFormContext();
  const assignmentTimeOffs = 'assignmentTimeOffs';
  useEffect(() => {
    const assignment = scenario?.assignment;
    const selectedScenarioTimeOffs: IPlacementMarginRequestTimeOff[] = getFormattedTimeOff(assignment?.timeOffs);
    dispatch(placementMarginAssignmentActions.setEditTimeOffs([]));
    if (scenario?.assignment?.timeOffs?.length > 0) {
      dispatch(placementMarginAssignmentActions.setEditTimeOffs(selectedScenarioTimeOffs));
    }
  }, [scenario?.assignment]);

  useEffect(() => {
    const initialColumns = getTimeOffGridColumns(t, isScenarioStatusPending, classes, editTimeOffs);
    setColumns(initialColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, isScenarioStatusPending, editTimeOffs]);

  const rightActions = useMemo<IXGridBulkActionButton[]>(() => {
    return [
      {
        KeyId: 'add-new-timeOff',
        Icon: AddIcon,
        disabled: !isScenarioStatusPending,
        onClick: e => addNewTimeOffRow(e),
        tooltipProps: {
          tooltipContent: t('marginTool.components.assignment.requestedTimeOff.grid.tootTips.addNew'),
        },
      },
      {
        KeyId: 'delete-existing-timeOff',
        Icon: DeleteOutlinedIcon,
        disabled: !isScenarioStatusPending || !selectedIds || selectedIds?.length === 0,
        onClick: () => deleteHandler(),
        tooltipProps: {
          tooltipContent: t('marginTool.components.assignment.requestedTimeOff.grid.tootTips.delete'),
        },
        rightDivider: false,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScenarioStatusPending, selectedIds, editTimeOffs]);

  const addNewTimeOffRow = e => {
    const existingRow = editTimeOffs?.slice(0);
    const newRow = {
      id: `${editTimeOffs?.length}`,
      timeOffId: null,
      startDate: '',
      endDate: '',
      days: '',
      hours: '',
      scenarioId: null,
      splitId: null,
      approvedBy: '',
    };
    existingRow.unshift(newRow);
    const formattedData = getFormattedTimeOff(existingRow);
    dispatch(placementMarginAssignmentActions.setEditTimeOffGridError(true));
    dispatch(placementMarginAssignmentActions.setEditTimeOffs(formattedData));
    setSelectedIds([]);
  };

  const deleteHandler = () => {
    const updatedDataAfterDelete = editTimeOffs?.filter(item => !selectedIds?.includes(item?.id));
    const formattedData = getFormattedTimeOff(updatedDataAfterDelete);
    dispatch(placementMarginAssignmentActions.setEditTimeOffs(formattedData));
    setSelectedIds([]);
    if (!checkOverlapForAllRecords(formattedData)) {
      dispatch(placementMarginAssignmentActions.setEditTimeOffGridError(false));
      register(assignmentTimeOffs);
      setValue(assignmentTimeOffs, formattedData, { shouldDirty: true });
    }
    triggerPeopleSoftCalculation(false, formattedData);
  };

  const getHeaderCount = timeOffEditRequests => {
    const totalRowCount = timeOffEditRequests?.length;
    const nullRowCount = timeOffEditRequests?.filter(
      eachRecord =>
        !isValid(new Date(eachRecord?.startDate)) ||
        !isValid(new Date(eachRecord?.endDate)) ||
        eachRecord?.hours === '' ||
        eachRecord?.hours === null,
    )?.length;
    setHeaderCount(totalRowCount - nullRowCount);
  };

  useEffect(() => {
    if (!isScenarioStatusPending) {
      setCurrentSortModel([]);
    }
    getHeaderCount(editTimeOffs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTimeOffs]);

  return (
    <>
      <Grid
        item
        xs={12}
        sx={theme => ({ paddingBottom: theme.spacing(4), maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden' })}
      >
        {isRefreshInProgress ? (
          <TimeOffSkeleton />
        ) : (
          <XGrid
            key={editTimeOffs.map(row => `${row.id}-${row.approvedBy}`).join('|')}
            rows={editTimeOffs || []}
            columns={columns}
            disableSelectionOnClick
            pagination={false}
            autoHeight={true}
            hideFooter
            editMode="row"
            title={t('marginTool.components.assignment.requestedTimeOff.grid.title')}
            onSortModelChange={model => {
              setCurrentSortModel(model);
            }}
            displayCustomCount={selectedIds?.length > 0 ? selectedIds?.length : headerCount}
            className={classes.expandedGrid}
            onSelectionModelChange={ids => {
              if (isScenarioStatusPending) setSelectedIds(ids);
            }}
            checkboxSelection={true}
            selectionModel={selectedIds}
            toolbarProps={{
              rightActions: rightActions,
              refreshButton: false,
              columnSelector: false,
              moreOption: false,
            }}
            components={{
              NoRowsOverlay: () => {
                return (
                  <div className="center" style={{ height: '100%' }}>
                    <Typography variant="subtitle1" justifyContent={'center'}>
                      {t('marginTool.components.assignment.requestedTimeOff.grid.noTimeOffs')}
                    </Typography>
                  </div>
                );
              },
            }}
          />
        )}
      </Grid>
    </>
  );
};

const TimeOffSkeleton = () => {
  return (
    <>
      {[1, 2, 3].map(row => (
        <Grid item xs={12} key={row}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Skeleton variant="rectangular" width="100%" height={10} />
            </Grid>
            <Grid item xs={3}>
              <Skeleton variant="rectangular" width="100%" height={10} />
            </Grid>
            <Grid item xs={3}>
              <Skeleton variant="rectangular" width="100%" height={10} />
            </Grid>
            <Grid item xs={3}>
              <Skeleton variant="rectangular" width="100%" height={10} />
            </Grid>
          </Grid>
        </Grid>
      ))}
    </>
  );
};
